import { ALARM_EVENT_KIND_LABELS, type AlarmDetail } from "@station/contracts";
import { Alert, Button, Card, EmptyState, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, SearchX } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { AcknowledgeButton, AssignDialog, NoteActionDialog } from "../../../features/alarms/alarm-actions.js";
import { alarmScopeText, relatedScreenLink, timelineDetail } from "../../../features/alarms/alarm-values.js";
import { useAlarm, useAlarmPermissions } from "../../../features/alarms/queries.js";
import {
  AlarmsPage,
  AlarmStatusBadge,
  AppLinkTo,
  Field,
  RelativeTime,
  SeverityBadge,
} from "../../../features/alarms/shared.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../lib/format.js";

export const Route = createFileRoute("/_app/alarmlar/$alarmId")({ component: AlarmDetailRoute });

/**
 * Alarm ayrıntısı (Prompt 20 web, `GET /alarms/:id`): başlık, ileti, kural (etiket + açıklama), kapsam, ilgili ekran
 * bağlantısı, zaman çizelgesi (tür, aktör — motor "Sistem" —, not, zaman) ve durum tablosuna göre eylemler
 * (`availableActions`: görüldü, ata, çöz, yeniden aç, not ekle). Görünmeyen alarm 404 → "bulunamadı".
 */
function AlarmDetailRoute(): ReactElement {
  const { t } = useTranslation();
  const { alarmId } = Route.useParams();
  const permissions = useAlarmPermissions();
  const alarm = useAlarm(alarmId, permissions.enabled && permissions.canRead);
  const title = alarm.data?.title ?? t("alarms.detail.title");
  return (
    <AlarmsPage
      title={title}
      {...(alarm.data ? { description: alarm.data.ruleLabel } : {})}
      breadcrumb={[
        {
          label: t("menu.alarms"),
          link: (children, className) => (
            <Link to="/alarmlar" className={className}>
              {children}
            </Link>
          ),
        },
        { label: title },
      ]}
      actions={alarm.data ? <AlarmActions alarm={alarm.data} /> : null}
    >
      {alarm.error ? (
        isApiError(alarm.error, "NOT_FOUND") ? (
          <EmptyState
            headingLevel={2}
            icon={<SearchX />}
            title={t("alarms.detail.notFoundTitle")}
            description={t("alarms.detail.notFoundDescription")}
            action={
              <Button asChild variant="outline">
                <Link to="/alarmlar">{t("alarms.detail.backToList")}</Link>
              </Button>
            }
          />
        ) : (
          <Alert tone="danger">{errorMessage(alarm.error)}</Alert>
        )
      ) : alarm.isPending ? (
        <SkeletonRows rows={6} />
      ) : (
        <AlarmDetailView alarm={alarm.data} />
      )}
    </AlarmsPage>
  );
}

function AlarmActions({ alarm }: { alarm: AlarmDetail }): ReactElement | null {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<"ASSIGN" | "RESOLVE" | "REOPEN" | "NOTE" | null>(null);
  if (!alarm.canManage || alarm.availableActions.length === 0) return null;
  const has = (action: AlarmDetail["availableActions"][number]) => alarm.availableActions.includes(action);
  return (
    <>
      {has("ACKNOWLEDGE") ? <AcknowledgeButton alarm={alarm} /> : null}
      {has("ASSIGN") ? (
        <Button variant={has("ACKNOWLEDGE") ? "outline" : "primary"} onClick={() => setDialog("ASSIGN")}>
          {alarm.assignee ? t("alarms.actions.reassign") : t("alarms.actions.assign")}
        </Button>
      ) : null}
      {has("RESOLVE") ? (
        <Button variant="outline" onClick={() => setDialog("RESOLVE")}>
          {t("alarms.actions.resolve")}
        </Button>
      ) : null}
      {has("REOPEN") ? (
        <Button variant="outline" onClick={() => setDialog("REOPEN")}>
          {t("alarms.actions.reopen")}
        </Button>
      ) : null}
      {has("NOTE") ? (
        <Button variant="ghost" onClick={() => setDialog("NOTE")}>
          {t("alarms.actions.addNote")}
        </Button>
      ) : null}
      <AssignDialog
        open={dialog === "ASSIGN"}
        onOpenChange={(open) => setDialog(open ? "ASSIGN" : null)}
        alarm={alarm}
      />
      {(["RESOLVE", "REOPEN", "NOTE"] as const).map((action) => (
        <NoteActionDialog
          key={action}
          open={dialog === action}
          onOpenChange={(open) => setDialog(open ? action : null)}
          alarm={alarm}
          action={action}
        />
      ))}
    </>
  );
}

function AlarmDetailView({ alarm }: { alarm: AlarmDetail }): ReactElement {
  const { t } = useTranslation();
  const related = relatedScreenLink(alarm.link, alarm.companyId);
  return (
    <>
      <Card className="grid gap-4 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <SeverityBadge severity={alarm.severity} />
          <AlarmStatusBadge status={alarm.status} />
          {alarm.autoResolved && alarm.status === "RESOLVED" ? (
            <StatusBadge tone="neutral">{t("alarms.autoResolved")}</StatusBadge>
          ) : null}
          {alarm.escalationLevel > 0 && alarm.status !== "RESOLVED" ? (
            <StatusBadge tone="neutral">
              {t("alarms.escalatedLevel", { level: alarm.escalationLevel })}
            </StatusBadge>
          ) : null}
        </div>
        <p className="whitespace-pre-line text-base">{alarm.message}</p>
        {related ? (
          <div>
            <Button asChild variant="outline" size="sm">
              <AppLinkTo link={related}>
                {t("alarms.detail.openRelated")}
                <ArrowUpRight aria-hidden="true" />
              </AppLinkTo>
            </Button>
          </div>
        ) : null}
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label={t("alarms.fields.rule")}>
            <span className="grid gap-0.5">
              <span>{alarm.ruleLabel}</span>
              <span className="text-xs font-normal text-muted-foreground">{alarm.ruleDescription}</span>
            </span>
          </Field>
          <Field label={t("alarms.fields.scope")}>{alarmScopeText(alarm, t)}</Field>
          <Field label={t("alarms.fields.occurrenceCount")}>
            <span className="tabular">{formatCount(alarm.occurrenceCount)}</span>
          </Field>
          <Field label={t("alarms.fields.firstOccurredAt")}>
            <span className="tabular">{formatDateTime(alarm.firstOccurredAt)}</span>
          </Field>
          <Field label={t("alarms.fields.lastOccurredAt")}>
            <span className="tabular">{formatDateTime(alarm.lastOccurredAt)}</span>
          </Field>
          <Field label={t("alarms.fields.acknowledged")}>
            {alarm.acknowledgedAt
              ? t("alarms.detail.byAt", {
                  name: alarm.acknowledgedBy?.fullName ?? "—",
                  at: formatDateTime(alarm.acknowledgedAt),
                })
              : "—"}
          </Field>
          <Field label={t("alarms.fields.assignee")}>
            {alarm.assignee
              ? t("alarms.detail.assignedBy", {
                  name: alarm.assignee.fullName,
                  by: alarm.assignedBy?.fullName ?? "—",
                  at: formatDateTime(alarm.assignedAt),
                })
              : "—"}
          </Field>
          <Field label={t("alarms.fields.resolved")}>
            {alarm.resolvedAt
              ? alarm.autoResolved
                ? t("alarms.detail.autoResolvedAt", { at: formatDateTime(alarm.resolvedAt) })
                : t("alarms.detail.byAt", {
                    name: alarm.resolvedBy?.fullName ?? "—",
                    at: formatDateTime(alarm.resolvedAt),
                  })
              : "—"}
          </Field>
          {alarm.escalatedAt && alarm.escalationLevel > 0 ? (
            <Field label={t("alarms.fields.escalation")}>
              {t("alarms.detail.escalation", {
                level: alarm.escalationLevel,
                at: formatDateTime(alarm.escalatedAt),
              })}
            </Field>
          ) : null}
        </dl>
        {alarm.resolutionNote ? (
          <div className="grid gap-1">
            <h2 className="text-sm font-semibold">{t("alarms.actions.resolutionNote")}</h2>
            <p className="whitespace-pre-line rounded-md bg-muted p-3 text-sm">{alarm.resolutionNote}</p>
          </div>
        ) : null}
        {!alarm.canManage ? (
          <p className="text-xs text-muted-foreground">{t("alarms.detail.readOnly")}</p>
        ) : null}
      </Card>
      <Card className="grid gap-3 p-5">
        <h2 className="text-base font-semibold">{t("alarms.detail.timeline")}</h2>
        {alarm.timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("alarms.detail.timelineEmpty")}</p>
        ) : (
          <ol className="grid gap-0" aria-label={t("alarms.detail.timeline")}>
            {alarm.timeline.map((entry) => {
              const details = timelineDetail(entry, t);
              return (
                <li
                  key={entry.id}
                  className="grid gap-1 border-l-2 border-border py-2 pl-4 first:pt-0 last:pb-0"
                >
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-medium">{ALARM_EVENT_KIND_LABELS[entry.kind]}</span>
                    <span className="text-sm text-muted-foreground">{entry.actor.name}</span>
                    <span className="text-xs text-muted-foreground">
                      <RelativeTime value={entry.createdAt} /> · {formatDateTime(entry.createdAt)}
                    </span>
                  </div>
                  {details.length > 0 ? <p className="text-sm">{details.join(" · ")}</p> : null}
                  {entry.note ? (
                    <p className="whitespace-pre-line rounded-md bg-muted p-2 text-sm">{entry.note}</p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}
      </Card>
    </>
  );
}
